/**
 * Mafia audio bus graph (Web Audio).
 * Buses: Master ← Music | SFX | UI
 * Volumes use decibels; sliders map 0..1 via linear→dB.
 * Royalty-free assets under /mafia/audio/ (procedural).
 */

export type AudioBusName = 'master' | 'music' | 'sfx' | 'ui'

const LINEAR_MIN = 0.0001

function linearToDb(linear: number): number {
  const v = Math.max(LINEAR_MIN, Math.min(1, linear))
  return 20 * Math.log10(v)
}

function dbToGain(db: number): number {
  return Math.pow(10, db / 20)
}

const ASSETS = {
  ambient: '/mafia/audio/ambient-loop.mp3',
  phase: '/mafia/audio/phase-swell.mp3',
  death: '/mafia/audio/death.mp3',
  vote: '/mafia/audio/vote.mp3',
  night: '/mafia/audio/night.mp3',
  click: '/mafia/audio/ui-click.mp3',
} as const

export type MafiaSfx = keyof Omit<typeof ASSETS, 'ambient'>

export class MafiaAudioEngine {
  private ctx: AudioContext | null = null
  private buses: Partial<Record<AudioBusName, GainNode>> = {}
  private ambientSource: AudioBufferSourceNode | null = null
  private ambientGain: GainNode | null = null
  private bufferCache = new Map<string, AudioBuffer>()
  private unlocked = false
  private muted = false
  private musicLinear = 0.35
  private sfxLinear = 0.7
  private uiLinear = 0.5
  private ducked = false

  async unlock(): Promise<void> {
    const ctx = this.ensureContext()
    if (ctx.state === 'suspended') await ctx.resume()
    this.unlocked = true
  }

  isUnlocked(): boolean {
    return this.unlocked
  }

  setMuted(muted: boolean) {
    this.muted = muted
    this.applyMaster()
  }

  isMuted(): boolean {
    return this.muted
  }

  /** 0..1 slider → Music bus dB */
  setMusicVolume(linear01: number) {
    this.musicLinear = linear01
    this.applyBus('music', linear01)
  }

  setSfxVolume(linear01: number) {
    this.sfxLinear = linear01
    this.applyBus('sfx', linear01)
  }

  setUiVolume(linear01: number) {
    this.uiLinear = linear01
    this.applyBus('ui', linear01)
  }

  getVolumes() {
    return {
      music: this.musicLinear,
      sfx: this.sfxLinear,
      ui: this.uiLinear,
      muted: this.muted,
    }
  }

  async startAmbient(): Promise<void> {
    if (!this.unlocked || this.muted) return
    await this.stopAmbient()
    const ctx = this.ensureContext()
    const buffer = await this.loadBuffer(ASSETS.ambient)
    const source = ctx.createBufferSource()
    source.buffer = buffer
    source.loop = true
    const gain = ctx.createGain()
    gain.gain.value = 1
    source.connect(gain)
    gain.connect(this.bus('music'))
    source.start(0)
    this.ambientSource = source
    this.ambientGain = gain
  }

  async stopAmbient(): Promise<void> {
    try {
      this.ambientSource?.stop()
    } catch {
      /* already stopped */
    }
    this.ambientSource?.disconnect()
    this.ambientGain?.disconnect()
    this.ambientSource = null
    this.ambientGain = null
  }

  /** Duck music under dialogue / important SFX (~-12 dB). */
  setDucking(active: boolean) {
    if (this.ducked === active || !this.ambientGain || !this.ctx) return
    this.ducked = active
    const now = this.ctx.currentTime
    const target = active ? dbToGain(-12) : 1
    this.ambientGain.gain.cancelScheduledValues(now)
    this.ambientGain.gain.setValueAtTime(this.ambientGain.gain.value, now)
    this.ambientGain.gain.linearRampToValueAtTime(target, now + (active ? 0.12 : 0.35))
  }

  async playSfx(name: MafiaSfx, bus: 'sfx' | 'ui' = 'sfx'): Promise<void> {
    if (!this.unlocked || this.muted) return
    const ctx = this.ensureContext()
    const buffer = await this.loadBuffer(ASSETS[name])
    const source = ctx.createBufferSource()
    source.buffer = buffer
    // slight pitch variation for organic repeats
    source.playbackRate.value = 0.94 + Math.random() * 0.12
    source.connect(this.bus(bus))
    source.start(0)
  }

  dispose() {
    void this.stopAmbient()
    void this.ctx?.close()
    this.ctx = null
    this.buses = {}
    this.unlocked = false
  }

  private ensureContext(): AudioContext {
    if (!this.ctx) {
      this.ctx = new AudioContext()
      const master = this.ctx.createGain()
      master.connect(this.ctx.destination)
      this.buses.master = master

      for (const name of ['music', 'sfx', 'ui'] as const) {
        const g = this.ctx.createGain()
        g.connect(master)
        this.buses[name] = g
      }
      this.applyBus('music', this.musicLinear)
      this.applyBus('sfx', this.sfxLinear)
      this.applyBus('ui', this.uiLinear)
      this.applyMaster()
    }
    return this.ctx
  }

  private bus(name: AudioBusName): GainNode {
    this.ensureContext()
    return this.buses[name]!
  }

  private applyBus(name: 'music' | 'sfx' | 'ui', linear01: number) {
    const node = this.buses[name]
    if (!node) return
    node.gain.value = dbToGain(linearToDb(linear01))
  }

  private applyMaster() {
    const master = this.buses.master
    if (!master) return
    master.gain.value = this.muted ? 0 : 1
  }

  private async loadBuffer(url: string): Promise<AudioBuffer> {
    const cached = this.bufferCache.get(url)
    if (cached) return cached
    const ctx = this.ensureContext()
    const res = await fetch(url)
    const raw = await res.arrayBuffer()
    const buffer = await ctx.decodeAudioData(raw.slice(0))
    this.bufferCache.set(url, buffer)
    return buffer
  }
}

let sharedEngine: MafiaAudioEngine | null = null

export function getMafiaAudioEngine(): MafiaAudioEngine {
  if (!sharedEngine) sharedEngine = new MafiaAudioEngine()
  return sharedEngine
}
